/**
 * waap-threat-log-detail-modal.tsx — Per-request access-log detail modal.
 *
 * Opens when an operator clicks a row in the Threat Investigation Details log
 * table. Fetches the full access-log record (vendor flat object ~100 fields)
 * via GET /waap/domains/:anchorId/analytics/threat-investigation/access-log/detail.
 *
 * The vendor's ClickHouse query runs 1-2 min on first call (cold WAAP SSO
 * session warm-up); the hook uses a 150 s timeout. Shows a spinner while
 * loading and surfaces the most operationally useful fields at the top.
 */

import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogHeader, DialogTitle, DialogBody, DialogFooter,
  Button, Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import type { WaapAccessLogArgs } from '@/api/act-as-waap';
import { useActAsWAAPAccessLogDetail } from '@/api/act-as-waap';
import { InfoGrid, SectionLabel } from '@/pages/operations/detail/tabs/ops-detail-shared';

/* ── Field display helpers ───────────────────────────────────────────────── */

/** Top-level fields surfaced prominently (ordered by operator relevance). */
const TOP_FIELDS = [
  'request_uuid',
  'host',
  'src_ip',
  'action',
  'status',
  'attack_type',
  'inject_attack_type',
  'inject_payload',
  'user_agent',
  'path',
  'upstream_addr',
  'upstream_response_time',
  'country',
  'timestamp',
] as const;

function DetailTable({ data }: { data: Record<string, unknown> }) {
  // Render top fields first, then all remaining keys alphabetically.
  const topSet = new Set<string>(TOP_FIELDS);
  const remaining = Object.keys(data)
    .filter((k) => !topSet.has(k))
    .sort();

  const topRows = TOP_FIELDS
    .filter((k) => data[k] != null && data[k] !== '')
    .map((k) => ({ label: k, value: String(data[k]) }));
  const remainingRows = remaining
    .filter((k) => data[k] != null && data[k] !== '')
    .map((k) => ({ label: k, value: String(data[k]) }));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <InfoGrid rows={topRows} />
      {remainingRows.length > 0 && (
        <>
          <SectionLabel>Additional fields</SectionLabel>
          <InfoGrid rows={remainingRows} />
        </>
      )}
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────────────────────────── */

interface WaapThreatLogDetailModalProps {
  anchorId: string;
  args: WaapAccessLogArgs | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Access-log detail modal — fetches and displays the full vendor record for
 * a single request UUID. Long cold-start expected (~1-2 min); shows spinner.
 */
export function WaapThreatLogDetailModal({
  anchorId,
  args,
  open,
  onOpenChange,
}: WaapThreatLogDetailModalProps) {
  const { t } = useTranslation();

  const detailQ = useActAsWAAPAccessLogDetail(anchorId, args, open);

  const title = args?.requestUuid
    ? t('waap_domain.analytics_threat_detail_title', 'Request Detail — {{id}}', { id: args.requestUuid })
    : t('waap_domain.analytics_threat_detail_title_generic', 'Request Detail');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle style={{ wordBreak: 'break-all' }}>{title}</DialogTitle>
      </DialogHeader>

      <DialogBody style={{ overflowY: 'auto', maxHeight: '60vh' }}>
        {detailQ.isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              {t(
                'waap_domain.analytics_threat_detail_loading',
                'Fetching log record — vendor ClickHouse query may take 1-2 minutes on first load…',
              )}
            </p>
            <Skeleton height={320} />
          </div>
        ) : detailQ.isError ? (
          <Alert tone="danger" title={t('waap_domain.analytics_threat_detail_error', 'Failed to load request detail')}>
            {String((detailQ.error as { message?: string })?.message ?? '')}
          </Alert>
        ) : detailQ.data === null ? (
          <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {t('waap_domain.analytics_threat_detail_not_found', 'Record not found or board not configured.')}
          </p>
        ) : detailQ.data ? (
          <DetailTable data={detailQ.data} />
        ) : null}
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('waap_domain.analytics_threat_detail_close', 'Close')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
